import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, afterEveryRender, computed, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, convertToParamMap } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { environment } from '../../../../../environments/environment';
import { TranslationService } from '@/layout/service/translation.service';
import { ScreenshotService } from '@/cloud/services/screenshot.service';

/** Room entry reported by the device in its check-in payload. */
interface DeviceRoom {
    /** Numeric room id — what the touch-panel UI's ?room= expects. Absent on older firmware. */
    id?: number;
    uniqueId: string;
    name: string;
    parentRoomId?: string;
}

/** Option for the room dropdown: value is the numeric id when the device reports one, else the uniqueId. */
interface RoomOption {
    name: string;
    value: number | string;
}

function readRooms(payload: unknown): DeviceRoom[] {
    if (!payload || typeof payload !== 'object' || !('rooms' in payload) || !Array.isArray(payload.rooms)) return [];
    return payload.rooms.flatMap((room: unknown) => {
        if (!room || typeof room !== 'object' || !('uniqueId' in room) || typeof room.uniqueId !== 'string' || !room.uniqueId) return [];
        const id = 'id' in room ? room.id : undefined;
        return [
            {
                uniqueId: room.uniqueId,
                name: 'name' in room && typeof room.name === 'string' ? room.name : room.uniqueId,
                id: typeof id === 'number' && Number.isSafeInteger(id) && id >= 0 ? id : undefined
            }
        ];
    });
}

/** Smallest the embedded panel gets on short windows; below that the page scrolls instead. */
const FILL_MIN_HEIGHT = 320;

/**
 * Embeds the device's touch-panel UI in the cloud portal via the tunnel proxy
 * (/api/devices/{id}/ui-proxy/). Used both as a device-page tab and as a
 * chrome-less full-window route (route data { fullWindow: true }).
 */
@Component({
    selector: 'app-cloud-ui-control',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, SelectModule],
    templateUrl: './cloud-ui-control.html',
    styleUrl: './cloud-ui-control.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloudUiControl {
    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private sanitizer = inject(DomSanitizer);
    private destroyRef = inject(DestroyRef);
    readonly deviceIdOverride = input<string | null>(null, { alias: 'deviceId' });
    readonly roomUniqueId = input<string | null>(null);
    readonly returnToRoom = input<{ namespaceId: string; locationId: string } | null>(null);
    readonly t = inject(TranslationService).t;
    private readonly container = viewChild<ElementRef<HTMLElement>>('container');
    private readonly panelFrame = viewChild<ElementRef<HTMLIFrameElement>>('panelFrame');
    private readonly screenshots = inject(ScreenshotService);

    /** Offer "Touch panel" in the report dialogs while this panel's frame is on screen. */
    private readonly registerPanel = effect((onCleanup) => {
        const frame = this.panelFrame()?.nativeElement;
        if (!frame) return;
        this.screenshots.registerPanel(frame);
        onCleanup(() => this.screenshots.unregisterPanel(frame));
    });
    /** Height that takes the panel down to the bottom of the window (less the page's bottom gutter); null until measured. */
    readonly fillHeight = signal<number | null>(null);
    /**
     * Grow the embedded panel to the bottom of the window wherever it is hosted (room tab or device page). Re-measured
     * after each render (the page above it can load, wrap or toggle panels, which moves it without resizing anything)
     * and when the window resizes.
     */
    private measureFill(): void {
        const el = this.container()?.nativeElement;
        if (!el || this.fullWindow() || !el.isConnected) return;
        // Keep whatever the host wraps the panel in (its margins, padding and the layout's bottom gutter) in view.
        let below = 0;
        for (let node: HTMLElement | null = el; node && !node.classList.contains('layout-wrapper'); node = node.parentElement) {
            const style = getComputedStyle(node);
            below += parseFloat(style.marginBottom) || 0;
            if (node !== el) below += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
        }
        const top = el.getBoundingClientRect().top + window.scrollY;
        const height = Math.max(FILL_MIN_HEIGHT, Math.floor(window.innerHeight - top - below));
        if (Math.abs((this.fillHeight() ?? 0) - height) > 1) this.fillHeight.set(height);
    }

    private readonly fillOnRender = afterEveryRender({ read: () => this.measureFill() });

    private readonly fillOnResize = (() => {
        const onResize = () => this.measureFill();
        window.addEventListener('resize', onResize);
        this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
        return onResize;
    })();
    private routeParams = toSignal(this.route.paramMap, { initialValue: this.route.snapshot.paramMap });
    private parentParams = toSignal(this.route.parent?.paramMap ?? of(convertToParamMap({})));
    private queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });

    deviceId = computed(() => this.deviceIdOverride() ?? this.routeParams().get('deviceId') ?? this.parentParams()?.get('deviceId') ?? '');
    fullWindow = computed(() => this.route.snapshot.data['fullWindow'] === true);
    private targetRoomUniqueId = computed(() => this.roomUniqueId() ?? this.queryParams().get('roomUniqueId'));
    tunnelConnected = signal<boolean | null>(null); // null = checking
    sessionError = signal<string | null>(null);
    iframeSrc = signal<SafeResourceUrl | undefined>(undefined);

    /** True once the device's web server answered the readiness probe — gates the iframe. */
    panelReady = signal(false);
    /** True while polling the proxy waiting for the device web server to come up. */
    probing = signal(false);
    /** Set when the device web server hasn't come up within the probe window. */
    probeTimedOut = signal(false);

    /** Rooms reported by the device in its check-in payload. */
    rooms = signal<DeviceRoom[]>([]);
    roomsLoaded = signal(false);
    roomLoadError = signal(false);
    /** Currently selected room (numeric id, or uniqueId string on older firmware), or null for the default room. */
    selectedRoomId = computed<number | string | null>(() => {
        const uniqueId = this.targetRoomUniqueId();
        if (uniqueId !== null) {
            const matches = this.rooms().filter((room) => room.uniqueId === uniqueId);
            return matches.length === 1 ? (matches[0].id ?? matches[0].uniqueId) : null;
        }
        const param = this.queryParams().get('room');
        return param ? (Number.isNaN(Number(param)) ? param : Number(param)) : null;
    });

    /** Dropdown options — prefer the numeric room id (what the panel UI's ?room= expects). */
    roomOptions = computed<RoomOption[]>(() => this.rooms().map((r) => ({ name: r.name, value: r.id ?? r.uniqueId })));

    showOfflinePlaceholder = computed(() => this.tunnelConnected() === false);
    showRoomSelect = computed(() => this.targetRoomUniqueId() === null && this.rooms().length > 1);
    roomMappingMissing = computed(() => this.targetRoomUniqueId() !== null && this.selectedRoomId() === null);

    private sessionToken: string | null = null;
    private sessionSubscription?: Subscription;
    private connectionGeneration = 0;
    private sessionGeneration = 0;
    private tunnelRevision = 0;
    private roomRevision = 0;
    private probeTimer: ReturnType<typeof setTimeout> | null = null;
    private probeDeadline = 0;
    /** Invalidates in-flight probes when a new poll starts or the component tears down. */
    private probeGeneration = 0;

    /** How often to re-probe while the device web server is starting. */
    private static readonly ProbeIntervalMs = 3_000;
    /** Give up and show the manual Retry state after this long. */
    private static readonly ProbeWindowMs = 120_000;

    private connectionEffect = effect((onCleanup) => {
        const id = this.deviceId();
        untracked(() => {
            const generation = ++this.connectionGeneration;
            this.resetSession();
            this.tunnelConnected.set(null);
            this.rooms.set([]);
            this.roomsLoaded.set(false);
            this.roomLoadError.set(false);
            if (!id) {
                this.sessionError.set('No control system selected');
                return;
            }
            const sub = this.api.subscribeToDevice(id).subscribe((device) => {
                if (device?.['payload']) this.receiveRooms(device['payload']);
                const connected: unknown = device?.['tunnelConnected'];
                if (typeof connected !== 'boolean') return;
                this.tunnelRevision++;
                this.onTunnelStateChange(connected);
            });
            if (!this.roomsLoaded()) void this.loadRooms(id, generation);
            void this.checkTunnelStatus();
            onCleanup(() => {
                this.connectionGeneration++;
                sub.unsubscribe();
                this.resetSession();
            });
        });
    });

    private selectionEffect = effect(() => {
        this.selectedRoomId();
        this.roomMappingMissing();
        untracked(() => this.rebuildIframe());
    });

    private receiveRooms(payload: unknown): void {
        this.roomRevision++;
        this.rooms.set(readRooms(payload));
        this.roomsLoaded.set(true);
        this.roomLoadError.set(false);
    }

    private async loadRooms(id = this.deviceId(), generation = this.connectionGeneration): Promise<void> {
        const revision = this.roomRevision;
        this.roomsLoaded.set(false);
        this.roomLoadError.set(false);
        try {
            const device = await this.api.getDevice(id);
            if (generation === this.connectionGeneration && revision === this.roomRevision) this.receiveRooms(device?.['payload']);
        } catch (error) {
            if (generation !== this.connectionGeneration || revision !== this.roomRevision) return;
            console.error('Failed to load control system rooms', error);
            this.roomLoadError.set(true);
            this.roomsLoaded.set(true);
        }
    }

    private async checkTunnelStatus(): Promise<void> {
        const generation = this.connectionGeneration;
        const revision = ++this.tunnelRevision;
        try {
            const status = await this.api.getDeviceTunnelStatus(this.deviceId());
            if (generation === this.connectionGeneration && revision === this.tunnelRevision) this.onTunnelStateChange(status.tunnelConnected);
        } catch (error) {
            if (generation !== this.connectionGeneration || revision !== this.tunnelRevision) return;
            console.error('Failed to check device tunnel status', error);
            this.sessionError.set('Could not check the device tunnel connection');
        }
    }

    private onTunnelStateChange(connected: boolean): void {
        if (connected === this.tunnelConnected()) return;
        this.tunnelConnected.set(connected);
        if (connected) {
            this.startSession();
        } else {
            this.resetSession();
        }
    }

    private startSession(): void {
        const id = this.deviceId();
        const generation = ++this.sessionGeneration;
        this.sessionSubscription?.unsubscribe();
        this.sessionError.set(null);
        this.sessionSubscription = this.api.createUiSession(id).subscribe({
            next: (session) => {
                if (generation !== this.sessionGeneration) return;
                this.sessionToken = session.token;
                // The tunnel being up doesn't mean the device's web server is listening yet
                // (it lags by tens of seconds after a reboot) — wait until it answers.
                this.pollPanelReady();
            },
            error: (err) => {
                if (generation !== this.sessionGeneration) return;
                console.error('Failed to create UI session', err);
                this.sessionError.set(err?.error?.error ?? 'Failed to create UI session');
            }
        });
    }

    /** Polls the proxy until the device web server answers, then shows the iframe. */
    private pollPanelReady(): void {
        this.clearProbe();
        this.panelReady.set(false);
        this.probing.set(true);
        this.probeTimedOut.set(false);
        this.probeDeadline = Date.now() + CloudUiControl.ProbeWindowMs;
        this.probeOnce(++this.probeGeneration);
    }

    private async probeOnce(generation: number): Promise<void> {
        const state = await this.api.probeUiProxyReady(this.deviceId());
        // Bail if a newer poll started, the tunnel dropped, or the component tore down
        if (generation !== this.probeGeneration || !this.probing()) return;

        switch (state) {
            case 'ready':
                this.probing.set(false);
                this.panelReady.set(true);
                this.rebuildIframe();
                break;
            case 'starting':
                if (Date.now() >= this.probeDeadline) {
                    this.probing.set(false);
                    this.probeTimedOut.set(true);
                    break;
                }
                this.probeTimer = setTimeout(() => this.probeOnce(generation), CloudUiControl.ProbeIntervalMs);
                break;
            case 'offline':
                this.probing.set(false);
                this.onTunnelStateChange(false);
                break;
            default:
                // Transient failures (network blip, 401 during token refresh, ...) keep
                // retrying until the deadline rather than aborting on the first miss.
                if (Date.now() >= this.probeDeadline) {
                    this.probing.set(false);
                    this.sessionError.set('Could not reach the touch panel UI on the device');
                    break;
                }
                this.probeTimer = setTimeout(() => this.probeOnce(generation), CloudUiControl.ProbeIntervalMs);
        }
    }

    private clearProbe(): void {
        if (this.probeTimer) {
            clearTimeout(this.probeTimer);
            this.probeTimer = null;
        }
    }

    private resetSession(): void {
        this.sessionGeneration++;
        this.sessionSubscription?.unsubscribe();
        this.sessionToken = null;
        this.probeGeneration++;
        this.clearProbe();
        this.probing.set(false);
        this.panelReady.set(false);
        this.probeTimedOut.set(false);
        this.sessionError.set(null);
        this.iframeSrc.set(undefined);
    }

    /** (Re)build the iframe src from the current session token and room selection. */
    private rebuildIframe(): void {
        if (!this.sessionToken || !this.panelReady() || this.roomMappingMissing()) {
            this.iframeSrc.set(undefined);
            return;
        }
        const id = this.deviceId();
        const baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');
        let src = `${baseUrl}/devices/${encodeURIComponent(id)}/ui-proxy/?uiToken=${encodeURIComponent(this.sessionToken)}`;
        const room = this.selectedRoomId();
        if (room !== null) src += `&room=${encodeURIComponent(String(room))}`;
        // Cache-bust the shell document so a freshly deployed UI is picked up.
        src += `&_cb=${Date.now()}`;
        this.iframeSrc.set(this.sanitizer.bypassSecurityTrustResourceUrl(src));
    }

    onRoomChange(roomId: number | string | null): void {
        // Keep the room in the route query so the view is shareable/restorable.
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { room: roomId },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    openFullScreen(): void {
        this.router.navigate(['/device', this.deviceId(), 'ui-control', 'full'], {
            queryParams: {
                ...this.route.snapshot.queryParams,
                room: this.selectedRoomId(),
                roomUniqueId: this.targetRoomUniqueId(),
                returnNamespace: this.returnToRoom()?.namespaceId ?? this.queryParams().get('returnNamespace'),
                returnLocation: this.returnToRoom()?.locationId ?? this.queryParams().get('returnLocation')
            }
        });
    }

    closeFullScreen(): void {
        const namespaceId = this.queryParams().get('returnNamespace');
        const locationId = this.queryParams().get('returnLocation');
        if (namespaceId && locationId) {
            this.router.navigate(['/dashboard', namespaceId, locationId], { queryParams: { tab: 'ui-control' } });
            return;
        }
        this.router.navigate(['/device', this.deviceId(), 'ui-control'], { queryParams: this.route.snapshot.queryParams });
    }

    retry(): void {
        if (this.roomMappingMissing()) void this.loadRooms();
        this.probeTimedOut.set(false);
        this.sessionError.set(null);
        if (this.tunnelConnected() && this.sessionToken) {
            this.pollPanelReady();
        } else if (this.tunnelConnected()) {
            // Tunnel is up but no session was minted (createUiSession failed) — try again
            this.startSession();
        } else {
            this.checkTunnelStatus();
        }
    }
}
